// Mapping from database names to display names
export const PORTFOLIO_DISPLAY_NAMES = {
    // DS Portfolios
    'DS30': 'DS 30 Portfolio',
    'DS50': 'DS 50 Portfolio',
    'DS70': 'DS 70 Portfolio',
    'DS90': 'DS 90 Portfolio',
    'DS100': 'DS 100 Portfolio',
    'DS100Plus': 'DS 100 Plus',
    // ETF Portfolios
    'DE50': 'Drummond ETF 50 Portfolio',
    'DE70': 'Drummond ETF 70 Portfolio',
    'DE90': 'Drummond ETF 90 Portfolio',
    'DE100': 'Drummond ETF 100 Portfolio',
    'DEDP': 'Drummond ETF Dynamic Portfolio',
    // Drummond Portfolios
    'DDP': 'Drummond Dynamic Portfolio',
    'DPM': 'Drummond Private Markets Portfolio',
    'DPC': 'Drummond Private Income Portfolio',
    // First Choice Portfolios
    'FirstChoiceConservative': 'First Choice Conservative',
    'FirstChoiceModerate': 'First Choice Moderate',
    'FirstChoiceBalanced': 'First Choice Balanced',
    'FirstChoiceGrowth': 'First Choice Growth',
    'FirstChoiceHighGrowth': 'First Choice High Growth',
    // Asset Classes
    'Cash': 'Cash',
    'GovernmentBonds': 'Government Bonds',
    'Credit': 'Credit',
    'AustralianEquities': 'Australian Equities',
    'GlobalEquitiesHedged': 'Global Equities Hedged',
    'GlobalEquitiesUnhedged': 'Global Equities Unhedged',
    'EmergingMarketEquities': 'Emerging Market Equities',
    'HedgeFunds': 'Hedge Funds',
    'REITs': 'REITs',
    'Infrastructure': 'Infrastructure',
};

// Define sort order for each portfolio
const PORTFOLIO_ORDER = {
    // DS Portfolios (1-10)
    'DS30': 1,
    'DS50': 2,
    'DS70': 3,
    'DS90': 4,
    'DS100': 5,
    'DS100Plus': 6,
    // ETF Portfolios (10-19)
    'DE50': 10,
    'DE70': 11,
    'DE90': 12,
    'DE100': 13,
    'DEDP': 14,
    // Drummond Portfolios (20-30)
    'DDP': 20,
    'DPM': 21,
    'DPC': 22,
    // First Choice Portfolios (40-50)
    'FirstChoiceConservative': 40,
    'FirstChoiceModerate': 41,
    'FirstChoiceBalanced': 42,
    'FirstChoiceGrowth': 43,
    'FirstChoiceHighGrowth': 44,
    // Asset Classes (60+)
    'Cash': 60,
    'GovernmentBonds': 61,
    'Credit': 62,
    'AustralianEquities': 63,
    'GlobalEquitiesHedged': 64,
    'GlobalEquitiesUnhedged': 65,
    'EmergingMarketEquities': 66,
    'HedgeFunds': 67,
    'REITs': 68,
    'Infrastructure': 69,
};

export const getDisplayName = (portfolioCode) => {
    return PORTFOLIO_DISPLAY_NAMES[portfolioCode] || portfolioCode;
};

export const sortPortfolios = (portfolios) => {
    return [...portfolios].sort((a, b) => {
        const orderA = PORTFOLIO_ORDER[a] ?? 999;
        const orderB = PORTFOLIO_ORDER[b] ?? 999;
        return orderA - orderB;
    });
};

/**
 * Build a display name map from the registry API response.
 * Returns { blender_code | sac_code: display_name } for all portfolios + SACs.
 *
 * The registry's `asset_classes` payload is the canonical source for sub-asset
 * class labels — entries are `{ code, label }` (label = admin-set sac_name
 * fallback to the SAC code itself). We merge those into the display map so
 * downstream `getDisplayName(code)` resolves to the friendly name.
 */
export const buildDisplayNameMap = (registry) => {
    if (!registry || !registry.suites) return { ...PORTFOLIO_DISPLAY_NAMES };
    const map = { ...PORTFOLIO_DISPLAY_NAMES };
    for (const suite of registry.suites) {
        for (const p of suite.portfolios) {
            if (p.blender_code) {
                map[p.blender_code] = p.display_name;
            }
        }
    }
    for (const entry of (registry.asset_classes || [])) {
        if (entry && typeof entry === 'object' && entry.code) {
            map[entry.code] = entry.label || entry.code;
        }
    }
    return map;
};

/**
 * Build portfolio categories (for optgroups) from the registry API response.
 * Returns array of { label, keys[] } matching the shape of hardcoded PORTFOLIO_CATEGORIES.
 */
export const buildPortfolioCategories = (registry, availableKeys) => {
    if (!registry || !registry.suites) return null;
    const available = availableKeys instanceof Set ? availableKeys : new Set(availableKeys || []);
    const categories = [];

    for (const suite of registry.suites) {
        const keys = suite.portfolios
            .map((p) => p.blender_code)
            .filter((k) => k && (available.size === 0 || available.has(k)));
        if (keys.length > 0) {
            categories.push({ label: suite.label, keys });
        }
    }

    // Asset Classes group — sourced from the live SubAssetClassConfig taxonomy.
    // Entries arrive as { code, label }; keep only the codes in the keys list
    // so the existing rendering code (which calls getDisplayName(k)) just
    // works. Friendly labels are resolved via buildDisplayNameMap above.
    //
    // Note: the `availableKeys` filter is NOT applied here. That filter exists
    // to hide model portfolios with no registered SAA (e.g. DS80 in the
    // registry but no portfolio_sac_weights rows). The /available-portfolios
    // endpoints that produce availableKeys only enumerate blender codes; they
    // never include SAC codes, so applying the filter here would silently
    // strip every taxonomy SAC from the dropdown. The taxonomy itself is the
    // authoritative source for which SACs are selectable.
    const assetClassEntries = registry.asset_classes || [];
    const assetClassKeys = assetClassEntries
        .map((entry) => (entry && typeof entry === 'object') ? entry.code : entry)
        .filter((k) => !!k);
    if (assetClassKeys.length > 0) {
        categories.push({ label: 'Asset Classes', keys: assetClassKeys });
    }

    return categories;
};
